import React, { useState, useEffect } from "react";
import { Heart, AlertCircle } from "lucide-react";
import {
  Card,
  CardContent,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselScrim,
} from "@/components/ui";
import { Skeleton } from "@/components/ui/Skeleton";
import { CarouselIndicator } from "@/components/CarouselIndicator";
import { useFunFacts } from "@/hooks/useFunFacts";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { CarouselApi } from "@/components/ui/Carousel";

interface FunFactsSectionProps {
  studentId: string;
  className?: string;
}

const COLORS = [
  "orange",
  "yellow",
  "lime",
  "green",
  "emerald",
  "sky",
  "blue",
  "indigo",
  "slate",
] as const;

const createColorPicker = () => {
  let availableColors = [...COLORS];

  return (): string => {
    if (availableColors.length === 0) {
      availableColors = [...COLORS];
    }

    const randomIndex = Math.floor(Math.random() * availableColors.length);
    const selectedColor = availableColors[randomIndex];

    availableColors.splice(randomIndex, 1);

    return selectedColor;
  };
};

const getColorClasses = (color: string) => ({
  background: `bg-${color}-50`,
  border: `border-${color}-200`,
  text: `text-${color}-600`,
});

const FunFactCard = ({
  question,
  answer,
  color,
}: {
  question: string;
  answer: string;
  color: string;
}): React.ReactElement => {
  const colorClasses = getColorClasses(color);

  return (
    <CarouselItem className="basis-auto pl-32px">
      <div
        className={cn(
          "flex h-[164px] w-[280px] flex-col rounded-lg border p-24px",
          colorClasses.background,
          colorClasses.border,
        )}
      >
        <h4
          className={cn(
            "mb-16px line-clamp-2 text-left text-sm leading-5 font-bold",
            colorClasses.text,
          )}
        >
          {question}
        </h4>
        <div className="flex flex-1 items-start">
          <p
            className={cn(
              "line-clamp-3 w-full text-base leading-[1.3] font-bold",
              colorClasses.text,
            )}
          >
            {answer}
          </p>
        </div>
      </div>
    </CarouselItem>
  );
};

const FunFactCardSkeleton = (): React.ReactElement => (
  <CarouselItem className="basis-auto pl-32px">
    <div className="flex h-[164px] w-[280px] flex-col rounded-lg border border-border bg-card p-24px">
      <Skeleton className="mx-auto mb-16px h-5 w-3/4" />
      <div className="flex flex-1 items-end">
        <div className="w-full">
          <Skeleton className="mb-1 h-6 w-full" />
          <Skeleton className="h-6 w-4/5" />
        </div>
      </div>
    </div>
  </CarouselItem>
);

const EmptyStateContainer = ({
  title,
  icon,
  message,
  className,
}: {
  title: string;
  icon: React.ReactNode;
  message: string;
  className?: string;
}): React.ReactElement => (
  <div className={className}>
    <h2 className="mb-4 text-base leading-none font-semibold tracking-tight text-card-foreground">
      {title}
    </h2>
    <div>
      <Card>
        <CardContent className="flex h-[184px] w-full flex-col items-center justify-center gap-4">
          {icon}
          <p className="text-center text-sm leading-[150%] font-medium text-muted-foreground">
            {message}
          </p>
        </CardContent>
      </Card>
    </div>
  </div>
);

const CarouselWrapper = ({
  children,
  setApi,
  testId,
  ariaLabel,
}: {
  children: React.ReactNode;
  setApi?: (api: CarouselApi | null) => void;
  testId: string;
  ariaLabel: string;
}): React.ReactElement => (
  <Carousel
    setApi={setApi}
    opts={{
      align: "start",
      slidesToScroll: 1,
      containScroll: "trimSnaps",
    }}
    className="relative w-full"
    data-testid={testId}
    aria-label={ariaLabel}
  >
    <CarouselScrim />
    <CarouselContent className="-ml-32px">{children}</CarouselContent>

    <CarouselPrevious
      data-testid="carousel-previous"
      className="absolute top-1/2 -left-2 -translate-y-1/2 rounded-full border p-2.5 shadow-md transition-opacity duration-300 disabled:pointer-events-none disabled:opacity-0"
    />
    <CarouselNext
      data-testid="carousel-next"
      className="absolute top-1/2 -right-2 -translate-y-1/2 rounded-full border p-2.5 shadow-md transition-opacity duration-300 disabled:pointer-events-none disabled:opacity-0"
    />
  </Carousel>
);

export const FunFactsSection = ({
  studentId,
  className,
}: FunFactsSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);
  const [cardColors, setCardColors] = useState<string[]>([]);

  const { funFacts, loading, error, hasFunFacts } = useFunFacts(studentId);

  // Assign colors when funFacts change
  useEffect(() => {
    if (funFacts.length > 0 && cardColors.length !== funFacts.length) {
      const colorPicker = createColorPicker();
      const colors = funFacts.map(() => colorPicker());
      setCardColors(colors);
    }
  }, [funFacts, cardColors.length]);

  useEffect(() => {
    if (!carouselApi) return;

    const updateCarouselState = (): void => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
      setTotalSlides(carouselApi.scrollSnapList().length);
    };

    updateCarouselState();
    carouselApi.on("select", updateCarouselState);

    return () => {
      carouselApi.off("select", updateCarouselState);
    };
  }, [carouselApi]);

  const scrollToSlide = (index: number): void => {
    carouselApi?.scrollTo(index);
  };

  if (loading) {
    return (
      <div className={className}>
        <h2 className="mb-4 text-[16px] leading-tight font-semibold text-card-foreground">
          {t("studentCard.funFacts.title")}
        </h2>
        <div className="relative flex w-full flex-col gap-2 rounded-xl">
          <CarouselWrapper
            testId="fun-facts-carousel"
            ariaLabel={t("studentCard.funFacts.title")}
          >
            {Array.from({ length: 3 }).map((_, index) => (
              <FunFactCardSkeleton key={index} />
            ))}
          </CarouselWrapper>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyStateContainer
        title={t("studentCard.funFacts.title")}
        icon={<AlertCircle className="h-12 w-12 text-muted-foreground" />}
        message={t("studentCard.funFacts.errorMessage")}
        className={className}
      />
    );
  }

  if (!hasFunFacts) {
    return (
      <EmptyStateContainer
        title={t("studentCard.funFacts.title")}
        icon={<Heart className="h-12 w-12 text-muted-foreground" />}
        message={t("studentCard.funFacts.emptyMessage")}
        className={className}
      />
    );
  }

  return (
    <div className={className}>
      <h2 className="mb-1 text-base leading-none font-semibold tracking-tight text-card-foreground">
        {t("studentCard.funFacts.title")}
      </h2>
      <p className="mb-4 text-sm text-muted-foreground">
        {t("studentCard.funFacts.subtitle")}
      </p>
      <div className="relative flex w-full flex-col gap-2 rounded-xl">
        <CarouselWrapper
          setApi={setCarouselApi}
          testId="fun-facts-carousel"
          ariaLabel={t("studentCard.funFacts.title")}
        >
          {funFacts.map((funFact, index) => (
            <FunFactCard
              key={index}
              question={funFact.question}
              answer={funFact.answer}
              color={cardColors[index] || "slate"}
            />
          ))}
        </CarouselWrapper>

        {/* Carousel indicators - only show if we have multiple slides */}
        {totalSlides > 1 && (
          <CarouselIndicator
            totalSlides={totalSlides}
            currentSlide={currentSlide}
            onDotClick={scrollToSlide}
            getAriaLabel={(slideNum) =>
              t("studentCard.funFacts.dotAria", { slideNum })
            }
          />
        )}
      </div>
    </div>
  );
};
